import React from 'react';
import { FormattedMessage } from 'react-intl';
import {
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
  parseISO
} from 'date-fns';
import Singleton from '../Singleton';
import { AuctionTimerType } from '../_shared/types/trade';

export default class TimerService extends Singleton<TimerService> {
  /**
   * Calculates the active timer dates
   */
  getTimeDifference = (activeExpiryTime: string) => {
    const now = new Date();
    const secondsLeft = differenceInSeconds(parseISO(activeExpiryTime), now);
    const minutesLeft = differenceInMinutes(parseISO(activeExpiryTime), now);
    const hoursLeft = differenceInHours(parseISO(activeExpiryTime), now);

    return { hoursLeft, minutesLeft, secondsLeft };
  };

  activeTimeFormat = (
    hoursLeft: number,
    minutesLeft: number,
    secondsLeft: number,
    isTimerExtended: boolean
  ) => {
    const auctionLengthInMinutes = 45;
    const secondsPostfix = secondsLeft - minutesLeft * 60;
    const secondsPostfixStr =
      secondsPostfix < 10 ? `0${secondsPostfix}` : secondsPostfix;

    let timeLeft;
    let alertState = false;
    let warnState = false;
    let extendedIndicator = false;

    if (hoursLeft > 1) {
      timeLeft = `${hoursLeft}hrs left`;
    } else if (hoursLeft === 1 || minutesLeft > auctionLengthInMinutes) {
      timeLeft = (
        <FormattedMessage
          id="vdp.bid.timeLeft"
          values={{ time: 1, unit: 'hr' }}
        />
      );
    } else if (minutesLeft >= 5) {
      timeLeft = (
        <FormattedMessage
          id="vdp.bid.timeLeft"
          values={{ time: minutesLeft, unit: 'min' }}
        />
      );
    } else if (minutesLeft >= 1) {
      timeLeft =
        secondsLeft > 0 ? (
          <FormattedMessage
            id="vdp.bid.timeLeft.countdown"
            values={{ minutes: minutesLeft, seconds: secondsPostfixStr }}
          />
        ) : (
          <FormattedMessage
            id="vdp.bid.timeLeft.countdown.minutes"
            values={{ minutes: minutesLeft }}
          />
        );
      alertState = true;
    }
    if (secondsLeft < 60 && secondsLeft >= 30) {
      timeLeft = (
        <>
          <FormattedMessage
            id="vdp.bid.timeLeft.countdown.seconds"
            values={{ seconds: secondsLeft }}
          />
          {!isTimerExtended && (
            <>
              &nbsp;
              <FormattedMessage id="vdp.bid.left" />
            </>
          )}
        </>
      );
      if (isTimerExtended) {
        extendedIndicator = true;
        warnState = true;
        alertState = false;
      } else {
        alertState = true;
        warnState = false;
      }
    } else if (secondsLeft < 30) {
      timeLeft = <FormattedMessage id="vdp.bid.under30s" />;
      warnState = true;
      alertState = false;
    }

    return { timeLeft, alertState, warnState, extendedIndicator };
  };

  pendingClosingTimeFormat = (hoursLeft: number, minutesLeft: number) => {
    let timeLeft;
    let alertState = false;
    let warnState = false;

    if (hoursLeft > 1) {
      // "2hrs left"
      timeLeft = (
        <FormattedMessage
          id="vdp.bid.timeLeft"
          values={{ time: hoursLeft, unit: 'hrs' }}
        />
      );
    } else if (hoursLeft === 1) {
      // "1hr left"
      timeLeft = (
        <FormattedMessage
          id="vdp.bid.timeLeft"
          values={{ time: 1, unit: 'hr' }}
        />
      );
    } else if (hoursLeft === 0) {
      if (minutesLeft >= 1) {
        const unit = minutesLeft > 1 ? 'mins' : 'min';
        // "[5-59]mins left"
        timeLeft = (
          <FormattedMessage
            id="vdp.bid.timeLeft"
            values={{ time: minutesLeft, unit }}
          />
        );
        // "[1-5]min left" but in red
        warnState = minutesLeft <= 5;
      } else {
        // "Under 1 min" in red
        timeLeft = <FormattedMessage id="vdp.bid.under1Min" />;
        alertState = true;
      }
    }

    return { timeLeft, alertState, warnState };
  };

  getFormattedTimeDifference = (
    expiryTime: string,
    variant = AuctionTimerType.ACTIVE,
    isTimerExtended: boolean
  ) => {
    const {
      hoursLeft,
      minutesLeft,
      secondsLeft: secondsDiff
    } = this.getTimeDifference(expiryTime);

    switch (variant) {
      case AuctionTimerType.PENDING:
        return this.pendingClosingTimeFormat(hoursLeft, minutesLeft);
      case AuctionTimerType.CLOSING:
        return this.pendingClosingTimeFormat(hoursLeft, minutesLeft);
      case AuctionTimerType.ACTIVE:
        return this.activeTimeFormat(
          hoursLeft,
          minutesLeft,
          secondsDiff,
          isTimerExtended
        );
      default:
        return this.activeTimeFormat(
          hoursLeft,
          minutesLeft,
          secondsDiff,
          isTimerExtended
        );
    }
  };
}
